import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { toast } from '@ifixit/ui/components/toast';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { callAjaxIO } from 'Shared/ajax_io';
import { apiErrorMessage } from 'Shared/editor/api_error_message';
import { Field as EditorField } from 'Shared/editor/field';

/** Selects the LanguageSwitcherFrameModule branch the AjaxIO handlers take. */
export type LanguageSwitchDoctype = 'wiki' | 'guide';

type LanguageSwitchError = {
   message: string;
   items?: Array<{ url: string; title: string }>;
};

type LanguageSwitchCheck = {
   switchAllowed: boolean;
   switchErrors: LanguageSwitchError[];
};

/** The server-side props both editors bootstrap for LanguageSection. */
export type LanguageSectionProps = {
   docid: number;
   currentLangid: string;
   currentLanguage: string;
   available: Record<string, string>;
   /**
    * Where a completed switch reopens the editor, keyed by target langid.
    * Absent means reload, which is right only when the doc keeps its site.
    */
   editUrls?: Record<string, string>;
};

function useCheckLanguageSwitch(doctype: LanguageSwitchDoctype, docid: number, fromLangid: string) {
   return useMutation({
      mutationFn: (toLangid: string) =>
         callAjaxIO<LanguageSwitchCheck>('checkErrors', doctype, docid, fromLangid, toLangid),
   });
}

/**
 * Empty means it worked. Wikis report success as null, not []:
 * WikiLangLib::switchWikiLang returns void, which AjaxIO sends as null.
 */
function useSwitchLanguage(doctype: LanguageSwitchDoctype, docid: number, fromLangid: string) {
   return useMutation({
      mutationFn: (toLangid: string) =>
         callAjaxIO<LanguageSwitchError[] | null>(
            'switchDocLanguage',
            doctype,
            docid,
            fromLangid,
            toLangid
         ),
   });
}

/**
 * Switching is near irreversible and other pages can block it, so the pick
 * only opens a dialog that asks the server first. Confirm saves immediately.
 */
export function LanguageSection({
   doctype,
   docid,
   currentLangid,
   currentLanguage,
   available,
   editUrls,
   onBeforeLeave,
}: LanguageSectionProps & {
   doctype: LanguageSwitchDoctype;
   /** Drops the page's unsaved-changes guard before the switch navigates. */
   onBeforeLeave: () => void;
}) {
   const [pending, setPending] = useState<string | null>(null);
   // switchGuideLang re-checks and returns its refusals rather than throwing,
   // so a guide can be refused after the check passed. Those carry the links.
   const [refused, setRefused] = useState<LanguageSwitchError[] | null>(null);
   const check = useCheckLanguageSwitch(doctype, docid, currentLangid);
   const switchLanguage = useSwitchLanguage(doctype, docid, currentLangid);

   const choose = (langid: string) => {
      setPending(langid);
      setRefused(null);
      check.mutate(langid);
   };

   const close = () => {
      setPending(null);
      setRefused(null);
      check.reset();
      switchLanguage.reset();
   };

   const confirm = () => {
      if (!pending) {
         return;
      }
      switchLanguage.mutate(pending, {
         onSuccess: returned => {
            if (returned && returned.length > 0) {
               setRefused(returned);
               return;
            }
            // Every loaded URL, title and revision id belongs to the old
            // language now. A toast here is torn down before it paints.
            onBeforeLeave();
            const destination = editUrls?.[pending];
            if (destination) {
               window.location.assign(destination);
            } else {
               window.location.reload();
            }
         },
         onError: (error: Error) => toast.error(apiErrorMessage(error)),
      });
   };

   const errors = refused ?? check.data?.switchErrors ?? [];
   const allowed = refused === null && (check.data?.switchAllowed ?? false);

   const title =
      doctype === 'guide'
         ? _js('Change this guide’s language?')
         : _js('Change this page’s language?');
   const description =
      doctype === 'guide'
         ? _js(
              'This moves the guide itself, not a translation of it: %1 stops being its language and %2 becomes it.',
              currentLanguage,
              (pending && available[pending]) ?? ''
           )
         : _js(
              'This moves the page itself, not a translation of it: %1 stops being its language and %2 becomes it.',
              currentLanguage,
              (pending && available[pending]) ?? ''
           );

   return (
      <div>
         <EditorField label={_js('Language')}>
            <Select value={currentLangid} onValueChange={choose}>
               <SelectTrigger className="w-full sm:max-w-xs">
                  <SelectValue />
               </SelectTrigger>
               <SelectContent>
                  <SelectItem value={currentLangid}>{currentLanguage}</SelectItem>
                  {Object.entries(available).map(([langid, name]) => (
                     <SelectItem key={langid} value={langid}>
                        {name}
                     </SelectItem>
                  ))}
               </SelectContent>
            </Select>
         </EditorField>

         <Dialog open={pending !== null} onOpenChange={open => !open && close()}>
            <DialogContent className="max-w-lg">
               <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
               </DialogHeader>

               {check.isPending && (
                  <p className="text-sm text-gray-500">{_js('Checking for problems…')}</p>
               )}
               {check.isError && (
                  <p className="text-sm text-red-600">{apiErrorMessage(check.error)}</p>
               )}
               {errors.length > 0 && (
                  <div className="flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                     {errors.map(error => (
                        <div key={error.message}>
                           <p>{error.message}</p>
                           {error.items && (
                              <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0">
                                 {error.items.map(item => (
                                    <li key={item.url}>
                                       <a
                                          href={item.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="underline"
                                       >
                                          {item.title}
                                       </a>
                                    </li>
                                 ))}
                              </ul>
                           )}
                        </div>
                     ))}
                  </div>
               )}
               {check.isSuccess && allowed && (
                  <p className="text-sm text-gray-500">{_js('This cannot be undone from here.')}</p>
               )}

               <DialogFooter>
                  <Button type="button" className="button-md button-outline" onClick={close}>
                     {_js('Cancel')}
                  </Button>
                  <Button
                     type="button"
                     disabled={!allowed || switchLanguage.isPending}
                     onClick={confirm}
                  >
                     {switchLanguage.isPending ? _js('Changing…') : _js('Change language')}
                  </Button>
               </DialogFooter>
            </DialogContent>
         </Dialog>
      </div>
   );
}
